import * as React from 'react';
/** @jsxImportSource @emotion/react */
import Img from 'react-cool-img';
import ReactPixel from 'react-facebook-pixel';
import TextPage from '../components/TextPage';
import PageSection from '../components/PageSection';
import CtaBanner from '../components/CtaBanner';
import Image_me from '../resources/images/me.jpg';
import Image_me_at_work from '../resources/images/me_at_work.jpg';
import Image_me_hunting from '../resources/images/me_hunting.jpg';
import Image_my_workspace from '../resources/images/my_workspace.jpg';
import { Link } from 'react-router-dom';

export function About() {
  return (
    <div>
      {ReactPixel.pageView()}
      <TextPage>
        <h1>The artist behind the magic</h1>
        <h2 className="quoteHighlight">
          I bring to life the creatures that lived only in your imagination —
          and the worlds they come from. Built from ink, observation, and the
          stubborn belief that the strange things are the true things.
        </h2>
        <p>
          I'm Panna, a fantasy illustrator based in Dublin, Ireland. I work in
          ink and pen — bold linework, vivid colour, dark fairy tale atmosphere
          — creating character portraits, creature designs, book covers and
          story art by hand, on paper.
        </p>
        <p>
          I was born and raised in Budapest, Hungary, but childhood adventures
          at Lake Balaton or along the river Danube made me feel at home in the
          wild — fishing with my dad, or sneaking away to catch water snakes and
          present them to my mother in a bouquet, to her greatest horror. I've
          always preferred first-hand experience with animals.
        </p>
        <p>
          But there was something else that fascinated me just as much: stories
          of dragons and magical beasts, myths and legends of old times. Books
          where the illustrations pulled you into another world entirely.
          Secretly, I always imagined all that magic was real. Just hidden.
        </p>
        <div className="imgBlock">
          <div className="imgContainer imgContainer--rotateLeft">
            <Img
              lazy
              cache
              alt="Panna in her awesome unicorn tee"
              src={Image_me}
            />
          </div>
          <div className="imgContainer imgContainer--rotateRight">
            <Img
              lazy
              cache
              alt="Panna at work while her boss is supervising the job done"
              src={Image_me_at_work}
            />
            <span>
              Panna at work while her boss is supervising the job done
            </span>
          </div>
        </div>

        <p>
          All my school textbooks were full of doodles — birds, horses, stags,
          and of course dragons, unicorns and gryphons. I always had a
          sketchbook with me, and it's probably safe to admit it was open during
          most classes.
        </p>
        <p>
          I turned this into a business in 2019, just before the pandemic. The
          lockdown left me one real option: I got out into the Irish wilderness.
          I found I was never truly isolated there — surrounded by life, colour,
          and story. I wanted to show what I was seeing. To make it real for
          other people.
        </p>
        <div className="imgContainer imgContainer--rotateLeft">
          <Img
            lazy
            cache
            alt="Panna sketching 'The Robin'"
            src={Image_my_workspace}
          />
          <span>Panna sketching "The Robin"</span>
        </div>
        <p>
          But how do I show what I see? Nature is beautiful and terrible —
          brutal, ruthless and dark, but also romantic, clever and full of
          wonders. That tension shaped my style: feminine but tough, pretty but
          with something shadowed underneath. Not black or white. Both. Yin and
          Yang.
        </p>
        <div className="imgContainer imgContainer--rotateRight">
          <Img
            lazy
            cache
            alt="Panna on the hunt for good reference photos at the Bull Island bird sanctuary"
            src={Image_me_hunting}
          />
          <span>Panna on the hunt for good reference photos</span>
        </div>
        <p>
          My work has always moved between wildlife and fantasy — the wild
          creatures of Ireland and Hungary on one side, dragons and mythical
          beasts on the other — and lately those two worlds have started
          bleeding into each other in ways I find genuinely interesting. There's
          wildness in every monster I draw, and something older and stranger in
          every animal.
        </p>
        <p>
          If you'd like to follow the adventure, you'll find me on{' '}
          <a
            href="https://www.facebook.com/pannadraws"
            target="_blank"
            rel="noopener noreferrer"
            title="PannaDraws on Facebook"
          >
            Facebook
          </a>{' '}
          and{' '}
          <a
            href="https://www.instagram.com/pannamatena/"
            target="_blank"
            rel="noopener noreferrer"
            title="PannaDraws on Instagram"
          >
            Instagram
          </a>
          , or you can <Link to="/#email-list">join my email list</Link> for
          behind-the-scenes work and early access to new pieces. And if my work
          speaks to you — if you have a story, a character, or a creature that
          deserves to exist on paper — I'd love to bring it to life.
        </p>
      </TextPage>
      <PageSection>
        <CtaBanner
          title="Commission a piece"
          buttonLabel="Illustration commissions"
          buttonHref="/illustrations"
          buttonVariant="primary"
          secondButton={{
            label: 'Characters & Creatures',
            href: '/characterCreatureDesign',
            variant: 'primary',
          }}
        />
      </PageSection>
    </div>
  );
}
